// Keeps a crash from leaving a blank page.
//  - After an update the old release's code files are gone, so a page still open from before asks for files that
//    no longer exist. That is fixed by loading the page again, once (not again within a minute, so a real outage
//    can't reload forever).
//  - Any other error shows what went wrong and a Reload button instead of an empty screen.
import { Component, type ReactNode } from 'react'
import { Button, Notice } from '../ui'

const RELOADED_AT = 'briary.reloadedForUpdate'

const isMissingCode = (e: unknown) =>
  /dynamically imported module|Importing a module script failed|error loading dynamically imported|Unable to preload CSS|ChunkLoadError|Loading chunk/i.test(
    String((e as Error)?.message ?? e),
  )

/** Reloads the page once for a release that has moved on; false when it already did so in the last minute. */
export function reloadForUpdate(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOADED_AT) || 0)
    if (Date.now() - last < 60_000) return false
    sessionStorage.setItem(RELOADED_AT, String(Date.now()))
  } catch {
    // no storage: reload anyway
  }
  window.location.reload()
  return true
}

// Vite reports a code file that failed to load here before the import throws.
window.addEventListener('vite:preloadError', (e) => {
  if (reloadForUpdate()) e.preventDefault()
})

export class CrashGuard extends Component<{ children: ReactNode }, { error: unknown }> {
  override state: { error: unknown } = { error: null }

  static getDerivedStateFromError(error: unknown) {
    return { error }
  }

  override componentDidCatch(error: unknown) {
    console.error(error)
    if (isMissingCode(error)) reloadForUpdate()
  }

  override render() {
    const { error } = this.state
    if (!error) return this.props.children
    const updated = isMissingCode(error)
    return (
      <div style={{ maxWidth: 640, margin: '64px auto', padding: '0 16px' }}>
        <Notice tone={updated ? 'info' : 'bad'}>
          {updated ? 'The hub has been updated. Loading the new version…' : `This page hit a problem: ${String((error as Error)?.message ?? error).slice(0, 300)}`}
        </Notice>
        <div style={{ marginTop: 12 }}>
          <Button onClick={() => window.location.reload()}>Reload</Button>
        </div>
      </div>
    )
  }
}
